import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { ButtonBase, InputBase, LinearProgress, Stack, Tooltip } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { LinkedinAccount } from '../linkedin-accounts/linkedin-accounts.types';
import { FilterOptions } from './funnel-inbox.types';
import LinkedinAccountAvatarCarousel from './linkedin-account-avatar-carousel';
import { InboxUpdateProgress } from './use-inbox-update';

interface Props {
  searchParam: string;
  searchFilters: string[];
  linkedinAccounts: LinkedinAccount[];
  activeLinkedinAccounts: string[];
  updateInboxLoading?: boolean;
  /** Live progress from useInboxUpdate — null when not updating */
  updateProgress?: InboxUpdateProgress | null;

  handleSearchParamChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSearchFiltersChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectAllLinkedinAccounts(): void;

  handleUnselectAllLinkedinAccounts(): void;

  handleSelectedLinkedinAccountChange(id: string): () => void;

  handleUpdateInbox?(): void;
}

const InboxHeader: React.FC<Props> = ({
  searchParam,
  searchFilters = [],
  linkedinAccounts = [],
  activeLinkedinAccounts = [],
  updateInboxLoading,
  updateProgress,
  handleSearchParamChange,
  handleSearchFiltersChange,
  handleSelectAllLinkedinAccounts,
  handleUnselectAllLinkedinAccounts,
  handleSelectedLinkedinAccountChange,
  handleUpdateInbox,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const progressPercent =
    updateProgress && updateProgress.totalCount > 0
      ? Math.round((updateProgress.updatedCount / updateProgress.totalCount) * 100)
      : 0;

  const progressLabel =
    updateProgress && updateProgress.totalCount > 0
      ? `${updateProgress.updatedCount}/${updateProgress.totalCount}`
      : null;

  const filterOptions: OptionType[] = [
    { label: t('activeCampaigns'), value: FilterOptions.ACTIVE_CAMPAIGNS },
    { label: t('notActiveCampaigns'), value: FilterOptions.NOT_ACTIVE_CAMPAIGNS },
    { label: t('archivedCampaigns'), value: FilterOptions.ARCHIVED_CAMPAIGNS },
  ];

  const isLinkedinAccountActive = (account: LinkedinAccount) => {
    return _.includes(activeLinkedinAccounts, account._id);
  };

  /**
   * The filter chips replace the former multi-select — the change handler contract stays untouched:
   * it still receives the complete list of active filters via `event.target.value`.
   */
  const handleToggleSearchFilter = (value: string) => {
    return () => {
      const updatedFilters = searchFilters.includes(value) ? searchFilters.filter((filter) => filter !== value) : [...searchFilters, value];

      handleSearchFiltersChange({ target: { value: updatedFilters } } as unknown as React.ChangeEvent<HTMLInputElement>);
    };
  };

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1.5} flexWrap={'wrap'}>
      <div className={classes.searchField}>
        <SearchIcon className={classes.searchIcon} />
        <InputBase
          className={classes.searchInput}
          value={searchParam}
          placeholder={t('searchCampaign')}
          disabled={updateInboxLoading}
          inputProps={{ autoComplete: 'off' }}
          onChange={handleSearchParamChange}
        />
      </div>

      {filterOptions.map((option) => {
        const isActive = searchFilters.includes(option.value);

        return (
          <ButtonBase
            key={option.value}
            className={cx(classes.filterChip, isActive ? 'active' : undefined)}
            disabled={updateInboxLoading}
            onClick={handleToggleSearchFilter(option.value)}
          >
            {option.label}
            {isActive ? <CloseIcon className={classes.filterChipIcon} /> : null}
          </ButtonBase>
        );
      })}

      <Stack className={classes.actions} direction={'row'} alignItems={'center'} gap={2}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          {linkedinAccounts.length !== activeLinkedinAccounts.length ? (
            <PaiLink text={t('selectAllAccounts')} size={'sm'} variant={'default'} underline={'always'} action={handleSelectAllLinkedinAccounts} />
          ) : (
            <PaiLink text={t('unselectAllAccounts')} size={'sm'} variant={'default'} underline={'always'} action={handleUnselectAllLinkedinAccounts} />
          )}
          <PaiHelpIcon tooltipText={t('manageMultipleChatsTooltip')} />
        </Stack>

        <LinkedinAccountAvatarCarousel
          linkedinAccounts={linkedinAccounts}
          isLinkedinAccountActive={isLinkedinAccountActive}
          handleSelectedLinkedinAccountChange={handleSelectedLinkedinAccountChange}
        />

        {activeLinkedinAccounts.length > 0 && handleUpdateInbox && (
          <Stack direction={'column'} gap={0.5}>
            <Tooltip
              title={updateInboxLoading && progressLabel ? t('inboxUpdateProgressTooltip', { label: progressLabel }) : ''}
              placement="bottom"
            >
              <span>
                <PaiButton
                  text={updateInboxLoading && progressLabel ? `${t('updating')} ${progressLabel}` : t('updateInbox')}
                  size="small"
                  color="secondary"
                  border="rounded"
                  startIcon={<RefreshIcon className={updateInboxLoading ? classes.spinningIcon : undefined} />}
                  loading={updateInboxLoading && !progressLabel}
                  action={handleUpdateInbox}
                  disabled={updateInboxLoading}
                  style={updateButtonStyle}
                />
              </span>
            </Tooltip>
            {updateInboxLoading && (
              <LinearProgress
                variant={progressLabel ? 'determinate' : 'indeterminate'}
                value={progressPercent}
                className={classes.progressBar}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

const updateButtonStyle: React.CSSProperties = {
  height: '40px',
  padding: '0 16px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const useStyles = makeStyles()((theme) => ({
  searchField: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: '300px',
    height: '40px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    borderRadius: '8px',
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.greys.medium,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    height: '100%',
    padding: '0 14px 0 42px',
    fontSize: theme.typography.pxToRem(15),
    '& input': {
      padding: 0,
    },
  },
  filterChip: {
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 14px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.dark,
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.active': {
      borderColor: theme.palette.primary.main,
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
    },
  },
  filterChipIcon: {
    fontSize: theme.typography.pxToRem(16),
  },
  actions: {
    marginLeft: 'auto',
  },
  progressBar: {
    borderRadius: 4,
    height: 3,
  },
  spinningIcon: {
    animation: 'spin 1.2s linear infinite',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
  },
}));

export default InboxHeader;
